import { get, chain, includes, map, mapValues, forIn, some, find, filter, size, capitalize, replace, orderBy, intersection, memoize } from "lodash"

export const GRACE_PERIOD = 30

export const BUNDLE_FILTER = {
  KEY: "offered_as",
  BUNDLE: "Bundle",
  STANDALONE: "Standalone",
}
// TODO: This is a stub function, will update when the model status
// workflow is finalized
export const getStatus = () => "unknown"

export const getCurrentRoute = () => {
  const { pathname } = window.location
  const tab = pathname.substr(pathname.lastIndexOf("/") + 1)
  if (!includes(["active", "browse"], tab)) {
    return "browse"
  }
  return tab
}

export const getTags = (datasets) => chain(datasets)
  .map((dataset) => [
    { type: "publisher", value: get(dataset, "publisher") },
    { type: "type", value: get(dataset, "type") },
  ])
  .flatten()
  .uniqBy(({ value }) => value)
  .value()

export const getTagValues = (datasets, isBundle = false) => {
  if (!datasets) { return {} }
  return ({
    [BUNDLE_FILTER.KEY]: [isBundle ? BUNDLE_FILTER.BUNDLE : BUNDLE_FILTER.STANDALONE],
    ...chain(getTags(datasets))
      .groupBy(({ type }) => type)
      .mapValues((item) => map(item, ({ value }) => value))
      .value(),
  })
}

export const getFilterValues = (filters) => {
  return mapValues(filters, (category) => chain(category)
    .map((on, key) => ({ key, on }))
    .filter(({ on }) => on)
    .map(({ key }) => key)
    .value(),
  )
}

export const getDynamicFilters = (datasets) => {
  // Get the weights of different filters aka number of occurences
  const weights = chain(datasets)
    .map((item) => item.isBundle ? getTags(item.datasets) : getTags([item]))
    .flatten()
    .sortBy(["type", "value"])
    .countBy((filter) => getFilterKey(filter))
    .value()

  // Dont add the bundle filter if there are no bundles in the datasets
  let bundleFilter = {}
  if (some(datasets, ({ isBundle }) => isBundle)) {
    bundleFilter = {
      [BUNDLE_FILTER.KEY]: [
        { type: BUNDLE_FILTER.KEY, value: BUNDLE_FILTER.BUNDLE },
        { type: BUNDLE_FILTER.KEY, value: BUNDLE_FILTER.STANDALONE },
      ],
    }
  }
  // Generate dynamic filters according to the datasets. The filters will be different between the default datasets and a search
  return {
    ...bundleFilter,
    ...chain(datasets)
      .map((item) => item.isBundle ? getTags(item.datasets) : getTags([item]))
      .flatten()
      .sortBy(["type", "value"])
      .sortedUniqBy(({ value }) => value)
      .groupBy("type")
      .mapValues((filters) => orderBy(filters, (filter) => get(weights, getFilterKey(filter)), ["desc"]))
      .value(),
  }
}

const isFilterOnFilterFn = (dynamicFilters, type) => (value, name) => value && find(get(dynamicFilters, type), (dFilter) => dFilter.value === name)

export const areFiltersResetable = (filters, dynamicFilters) => {
  let resetable = false
  forIn(filters, (filter, type) => {
    // as soon as we find a true value and it's part of the dynamicFilters(aka relevant filters), its resetable
    if (some(filter, isFilterOnFilterFn(dynamicFilters, type))) {
      resetable = true
      return false
    }
  })
  return resetable
}

export const getNbActiveFilters = (filters, dynamicFilters) => {
  let count = 0
  forIn(filters, (f, type) => {
    // only count filters if they're part of the dynamic filters (search can modify these)
    count += size(filter(f, isFilterOnFilterFn(dynamicFilters, type)))
  })
  return count
}

export function getFilterTitle(key) {
  return capitalize(replace(key, /_/g, " "))
}

export function getFilterKey(filter) {
  return `${get(filter, "type")}|${get(filter, "value")}`
}

export const memoizedTrackingValues = memoize((filter) => {
  return chain(getFilterValues(filter))
    .map(f => f)
    .flatten()
    .value()
}, (...args) => JSON.stringify(args[0]))

export const memoizedFilteredDatasets = memoize((filters, query, searched) => {
  const filterValues = getFilterValues(filters)

  return filter(searched, (item) => {
    const tags = item.isBundle ? getTagValues(item.datasets, true) : getTagValues([item])
    let show = true
    forIn(filterValues, (category, key) => {
      if (size(category) > 0 && size(intersection(category, tags[key])) === 0) {
        show = false
        return false
      }
    })
    return show
  })
}, (...args) => JSON.stringify({ filter: args[0], query: args[1] }))
